import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import notifiers from "global/constants/NotificationConstants";
import strings from "global/constants/StringConstants";
import { openErrorNotification } from "helpers/methods";
import { SignRequestFieldData } from "models/interfaces";
import {
  downloadRequestFieldDataCsv,
  fetchRequestFieldData,
} from "../MySignRequestService";
import viewSignRequestStyles from "./ViewSignRequest.styles";
import viewSignRequestTokens from "./ViewSignRequest.tokens";
import { ChevronDownGlyph, DownloadGlyph } from "./ViewRequestIcons";
import { formatAuditDateTime } from "./ViewRequestStatusMeta";

const styles = viewSignRequestStyles;
const { documentRow } = viewSignRequestTokens;

interface CollectedDataCardProps {
  requestId: string;
  /** Who filled what — shown per row so the sender can tell the signers apart. */
  signerNameByEmail?: Record<string, string>;
}

/** Hands a fetched blob to the browser as a download. */
const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Checkbox values are stored as "true"/"false"; everything else is shown as typed. */
const displayValue = (row: SignRequestFieldData) => {
  if (row.fieldType === strings.checkbox) {
    return row.value === "true" ? "Checked" : "Not checked";
  }
  return row.value || "";
};

/**
 * The values signers typed into the sender's custom fields (text,
 * date, dropdown, checkbox, radio), one row per field, with a CSV export.
 * Mirrors AttachmentsCard: hidden until loaded, and hidden entirely when the
 * request collected nothing.
 */
const CollectedDataCard: React.FC<CollectedDataCardProps> = ({
  requestId,
  signerNameByEmail,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [rows, setRows] = useState<SignRequestFieldData[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!requestId) return undefined;
    (async () => {
      try {
        const response = await fetchRequestFieldData(requestId);
        if (!cancelled) setRows(response);
      } catch (error: any) {
        // No toast: a request without custom fields is the common case. A
        // real failure still reaches the console.
        console.error("Failed to load collected field data", error);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  const handleExport = async () => {
    setExporting(true);
    try {
      saveBlob(
        await downloadRequestFieldDataCsv(requestId),
        `${requestId}-field-data.csv`,
      );
    } catch {
      openErrorNotification(notifiers.ATTACHMENT_DOWNLOAD_FAILED);
    } finally {
      setExporting(false);
    }
  };

  if (!loaded || rows.length === 0) return null;

  const signerLabel = (row: SignRequestFieldData) => {
    const email = row.signerEmail || "";
    const name = signerNameByEmail?.[email.toLowerCase()];
    return name || email;
  };

  // Group by document so a multi-file request reads in the same order as the
  // documents card above it.
  const fileNames = Array.from(new Set(rows.map((row) => row.fileName)));
  const showFileHeadings = fileNames.length > 1;

  return (
    <Box sx={styles.card}>
      <Box sx={styles.sectionHeader(false)}>
        <Box sx={styles.sectionHeaderLead}>
          <Box component="h2" sx={styles.sectionTitle}>
            Collected Data
          </Box>
          <Box component="span" sx={styles.sectionCount()}>
            {rows.length}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            disableRipple
            disabled={exporting}
            onClick={handleExport}
            aria-label="Export collected data as CSV"
            sx={styles.documentAction}
          >
            <DownloadGlyph
              sx={{
                width: documentRow.actionIconSize,
                height: documentRow.actionIconSize,
              }}
            />
            Export CSV
          </Button>
          <IconButton
            disableRipple
            onClick={() => setExpanded((value) => !value)}
            aria-label={
              expanded ? "Collapse collected data" : "Expand collected data"
            }
            sx={styles.sectionToggle(expanded)}
          >
            <ChevronDownGlyph sx={styles.sectionToggleGlyph} />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {fileNames.map((fileName) => (
          <Box key={fileName}>
            {showFileHeadings && (
              <Typography
                component="div"
                sx={{ ...styles.documentMeta, px: 2, pt: 1.5 }}
              >
                {fileName}
              </Typography>
            )}
            {rows
              .filter((row) => row.fileName === fileName)
              .map((row) => {
                const meta = [
                  signerLabel(row),
                  row.filledOn ? formatAuditDateTime(row.filledOn) : "",
                  row.page != null ? `Page ${row.page + 1}` : "",
                ]
                  .filter(Boolean)
                  .join(" · ");
                const value = displayValue(row);
                return (
                  <Box key={row.id} sx={styles.documentRow}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography component="div" sx={styles.documentMeta}>
                        {row.label || row.fieldName}
                      </Typography>
                      <Tooltip title={value} arrow describeChild>
                        <Typography component="div" sx={styles.documentName}>
                          {value || "—"}
                        </Typography>
                      </Tooltip>
                      <Typography component="div" sx={styles.documentMeta}>
                        {meta}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
          </Box>
        ))}
      </Collapse>
    </Box>
  );
};

export default CollectedDataCard;
